/**
 * Shared Google Maps reverse-geocode helpers for consistent address parsing
 * (street number + route order, best result among multiple types).
 */

export function normalizeLatLng(lat, lng) {
  const la = typeof lat === "string" ? parseFloat(lat) : Number(lat);
  const ln = typeof lng === "string" ? parseFloat(lng) : Number(lng);
  if (!Number.isFinite(la) || !Number.isFinite(ln)) return null;
  return { lat: la, lng: ln };
}

const RESULT_TYPE_PRIORITY = [
  "street_address",
  "premise",
  "establishment",
  "point_of_interest",
  "subpremise",
  "route",
  "intersection",
  "neighborhood",
  "sublocality",
  "locality",
  "administrative_area_level_3",
  "administrative_area_level_2",
  "administrative_area_level_1",
  "political",
];

/**
 * Picks the most specific reverse-geocode result; `results[0]` is not always
 * the best street-level address (e.g. can be a large political area).
 */
export function pickReverseGeocodeResult(results) {
  if (!Array.isArray(results) || !results.length) return null;

  const scoreResult = (r) => {
    let typeScore = 900;
    for (let i = 0; i < RESULT_TYPE_PRIORITY.length; i++) {
      if (r.types?.includes(RESULT_TYPE_PRIORITY[i])) {
        typeScore = i;
        break;
      }
    }
    const partialPenalty = r.partial_match ? 100 : 0;
    return typeScore * 10 + partialPenalty;
  };

  return [...results].sort((a, b) => scoreResult(a) - scoreResult(b))[0];
}

export function parseGoogleAddressComponents(addressComponents) {
  if (!Array.isArray(addressComponents)) {
    return {
      country: "",
      province: "",
      city: "",
      community: "",
      postalCode: "",
      streetAddress: "",
      iso2: "",
    };
  }

  let country = "";
  let province = "";
  let city = "";
  let community = "";
  let postalCode = "";
  let streetNumber = "";
  let route = "";
  let sublocality = "";
  let neighborhood = "";

  addressComponents.forEach((component) => {
    const types = component.types;
    if (types.includes("country")) country = component.long_name;
    if (types.includes("administrative_area_level_1")) province = component.long_name;
    if (types.includes("locality")) city = component.long_name;
    if (types.includes("postal_town") && !city) city = component.long_name;
    if (types.includes("sublocality") || types.includes("sublocality_level_1")) {
      sublocality = component.long_name;
    }
    if (types.includes("neighborhood")) neighborhood = component.long_name;
    if (types.includes("postal_code")) postalCode = component.long_name;
    if (types.includes("route")) route = component.long_name;
    if (types.includes("street_number")) streetNumber = component.long_name;
    if (types.includes("administrative_area_level_2") && !community) {
      community = component.long_name;
    }
  });

  if (!city) {
    city =
      sublocality ||
      neighborhood ||
      addressComponents.find((c) => c.types.includes("administrative_area_level_3"))?.long_name ||
      addressComponents.find((c) => c.types.includes("administrative_area_level_2"))?.long_name ||
      "";
  }

  if (!community && (sublocality || neighborhood)) {
    community = sublocality || neighborhood;
  }

  const streetAddress = [streetNumber, route].filter(Boolean).join(" ").trim();

  const iso2 = addressComponents.find((c) => c.types.includes("country"))?.short_name || "";

  return { country, province, city, community, postalCode, streetAddress, iso2 };
}

/** Rough continent bucket from ISO 3166-1 alpha-2 (matches existing Account Setting behavior). */
export function iso2ToContinent(iso2) {
  const m = (iso2 || "").toUpperCase();
  const NA = ["US", "CA", "MX"];
  const SA = ["BR", "AR", "CL", "CO", "PE", "UY", "PY", "BO", "VE", "EC"];
  const EU = ["GB", "FR", "DE", "ES", "IT", "NL", "SE", "NO", "FI", "PL", "PT", "IE", "AT", "GR", "CZ"];
  const AS = ["PK", "IN", "CN", "JP", "AE", "SA", "SG", "TH", "ID", "MY", "BD", "PH", "KR"];
  const AF = ["ZA", "NG", "EG", "KE", "MA", "TZ", "UG", "DZ"];
  const OC = ["AU", "NZ", "FJ", "PG"];
  if (NA.includes(m)) return "North America";
  if (SA.includes(m)) return "South America";
  if (EU.includes(m)) return "Europe";
  if (AS.includes(m)) return "Asia";
  if (AF.includes(m)) return "Africa";
  if (OC.includes(m)) return "Oceania";
  return "Unknown";
}
